// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button } from '@loa/ui-components';
import { openItemsApi, type OpenItem } from '../../api/openItemsApi';

/**
 * Panel 3 of the relationship Overview — the Open Items checklist. Merges
 * platform-derived items (read-only, auto-resolving) with manual items the
 * advisor can mark complete / dismiss, plus an "Add item" control. Renders
 * nothing when the feature is disabled (list is null). See
 * docs/specs/relationship-summary-panels-prd.md §7.
 */
export function OpenItemsPanel({ profileId }: { profileId: string }) {
  const [items, setItems] = useState<OpenItem[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  // The cancelled flag stops state updates landing after unmount — without it
  // the trailing setLoaded fires into a torn-down environment (an unhandled
  // "window is not defined" rejection under vitest when the page unmounts
  // before the list resolves).
  const fetchOnce = useCallback(async (cancelled?: { current: boolean }) => {
    try {
      const list = await openItemsApi.list(profileId);
      if (cancelled?.current) return;
      setItems(list);
      setError(null);
    } catch {
      if (cancelled?.current) return;
      setError('Could not load open items.');
    } finally {
      if (!cancelled?.current) {
        setLoaded(true);
      }
    }
  }, [profileId]);

  useEffect(() => {
    const cancelled = { current: false };
    void fetchOnce(cancelled);
    return () => {
      cancelled.current = true;
    };
  }, [fetchOnce]);

  const setStatus = useCallback(
    async (item: OpenItem, status: 'COMPLETE' | 'DISMISSED') => {
      if (!item.id) return;
      setBusyId(item.id);
      try {
        await openItemsApi.updateStatus(profileId, item.id, status);
        setItems((prev) => (prev ?? []).filter((x) => x.id !== item.id));
      } catch {
        setError('Could not update that item.');
      } finally {
        setBusyId(null);
      }
    },
    [profileId],
  );

  const add = useCallback(async () => {
    const title = draft.trim();
    if (!title) return;
    setAdding(true);
    try {
      const created = await openItemsApi.add(profileId, title);
      setItems((prev) => [...(prev ?? []), created]);
      setDraft('');
    } catch {
      setError('Could not add that item.');
    } finally {
      setAdding(false);
    }
  }, [draft, profileId]);

  // Feature disabled (null) or not loaded yet → render nothing.
  if (!loaded || items === null) {
    return null;
  }

  return (
    <div
      data-testid="open-items-panel"
      className="bg-white rounded-card p-lg mb-md"
      style={{ border: '1px solid #F0F0F0' }}
    >
      <h3 className="text-[18px] font-semibold text-black m-0 mb-md">Open Items</h3>
      {error && <p className="text-[13px] text-[#c62828] m-0 mb-sm">{error}</p>}
      {items.length === 0 && (
        <p data-testid="open-items-empty" className="text-[13px] text-[#8C8C8C] m-0 mb-[12px]">
          Nothing outstanding.
        </p>
      )}
      <ul className="flex flex-col gap-sm m-0 p-0 list-none">
        {items.map((item, i) => (
          <li
            key={item.id ?? `derived-${i}`}
            data-testid="open-item-row"
            className="flex items-start gap-[10px]"
          >
            {item.derived ? (
              <Badge color="grey" size="sm" data-testid="open-item-derived-badge">Auto</Badge>
            ) : (
              <button
                type="button"
                data-testid="open-item-complete"
                aria-label="Mark complete"
                disabled={busyId === item.id}
                onClick={() => setStatus(item, 'COMPLETE')}
                className="mt-[2px] w-[16px] h-[16px] rounded-[4px] border border-[#BDB8FF] bg-white cursor-pointer shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <span className="text-body-03 text-black">{item.title}</span>
              {item.detail && <span className="text-[13px] text-[#8C8C8C]"> — {item.detail}</span>}
            </div>
            {!item.derived && (
              <button
                type="button"
                data-testid="open-item-dismiss"
                disabled={busyId === item.id}
                onClick={() => setStatus(item, 'DISMISSED')}
                className="text-body-04 text-[#8C8C8C] bg-transparent border-none cursor-pointer"
              >
                Dismiss
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-sm mt-md">
        <input
          data-testid="open-item-add-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add();
          }}
          placeholder="Add an item…"
          className="flex-1 h-[36px] px-[12px] rounded-lg border border-[#E0E0E0] text-body-03"
        />
        <Button size="sm" onClick={add} disabled={adding || draft.trim().length === 0} data-testid="open-item-add-button">
          {adding ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}

export default OpenItemsPanel;
